import { Capacitor } from '@capacitor/core';

const NATIVE = Capacitor.isNativePlatform();
/**
 * Audio cues, ported from the plugin. Host-only by contract (the caller
 * checks); followers get haptics via the native layer instead.
 *
 * iOS needs a real (if inaudible) buffer played inside a user gesture
 * before any later sound will play — `initAudioArming()` catches the very
 * first tap. While the timer runs, a looping 1-frame silent buffer keeps
 * the AudioContext warm so period cues fire with the screen locked.
 */
let _ctx: AudioContext | null = null;
let _armed = false;
let _silent: AudioBufferSourceNode | null = null;
const listeners = new Set<(armed: boolean) => void>();

function ctx(): AudioContext {
  if (!_ctx) {
    const AC = (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext) as typeof AudioContext;
    _ctx = new AC();
  }
  return _ctx;
}

export function isAudioArmed(): boolean {
  return _armed || Capacitor.isNativePlatform(); // the native player needs no tap
}
export function onAudioArmed(fn: (armed: boolean) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function armAudio(): void {
  if (_armed) return;
  try {
    const c = ctx();
    void c.resume();
    const buf = c.createBuffer(1, 1, c.sampleRate);
    const src = c.createBufferSource();
    src.buffer = buf;
    src.connect(c.destination);
    src.start(0);
    _armed = true;
    listeners.forEach((l) => l(true));
  } catch {
    /* no audio on this device */
  }
}

export function initAudioArming(): void {
  const evs = ['pointerdown', 'touchend', 'keydown', 'click'];
  const arm = () => {
    armAudio();
    if (_armed) evs.forEach((ev) => document.removeEventListener(ev, arm, true));
  };
  evs.forEach((ev) => document.addEventListener(ev, arm, true));
}

export function startSilentLoop(): void {
  if (NATIVE) { prepareCueSounds(); return; } // native plays through its own player
  try {
    const c = ctx();
    void c.resume();
    if (_silent) {
      try {
        _silent.stop();
      } catch {
        /* already stopped */
      }
    }
    const buf = c.createBuffer(1, 1, c.sampleRate);
    const src = c.createBufferSource();
    src.buffer = buf;
    src.loop = true;
    src.connect(c.destination);
    src.start(0);
    _silent = src;
  } catch {
    /* ignore */
  }
}
export function stopSilentLoop(): void {
  try {
    _silent?.stop();
  } catch {
    /* ignore */
  }
  _silent = null;
}

function beep(freq: number, dur: number, vol = 0.4, type: OscillatorType = 'sine'): void {
  try {
    const c = ctx();
    if (c.state !== 'running') void c.resume(); // backgrounding suspends it
    const osc = c.createOscillator();
    const gain = c.createGain();
    osc.connect(gain);
    gain.connect(c.destination);
    osc.type = type;
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(vol, c.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, c.currentTime + dur);
    osc.start(c.currentTime);
    osc.stop(c.currentTime + dur);
  } catch {
    /* ignore */
  }
}

function vibrate(pattern: number[]): void {
  try {
    navigator.vibrate?.(pattern);
  } catch {
    /* ignore */
  }
}

// ─── Native app: the phone's own audio player ───────────────────────────────
// In the iOS app Web Audio is muted by the ring/silent switch and is cut off
// whenever the app goes to the background (it comes back "interrupted"), so
// cues went silent. The native player runs as a playback app (plays through
// the silent switch, like a music app) and needs no tap to arm. The three
// sounds are the same beeps as below, rendered to public/sounds/*.wav.
type Cue = 'period' | 'warning' | 'done';
let nativeReady: Promise<boolean> | null = null;
function nativeAudio(): Promise<boolean> {
  if (!NATIVE) return Promise.resolve(false);
  if (!nativeReady) {
    nativeReady = (async () => {
      try {
        const { NativeAudio } = await import('@capacitor-community/native-audio');
        await NativeAudio.configure({ focus: true, fade: false });
        for (const id of ['period', 'warning', 'done'] as Cue[]) {
          await NativeAudio.preload({ assetId: id, assetPath: `public/sounds/${id}.wav`, audioChannelNum: 1, isUrl: false }).catch(() => undefined);
        }
        return true;
      } catch (e) {
        console.warn('[audio] native audio unavailable', e);
        return false;
      }
    })();
  }
  return nativeReady;
}
/** Warm the native player early (preload), so the first cue isn't late. */
export function prepareCueSounds(): void {
  void nativeAudio();
}
async function playNative(id: Cue): Promise<boolean> {
  if (!(await nativeAudio())) return false;
  try {
    const { NativeAudio } = await import('@capacitor-community/native-audio');
    await NativeAudio.play({ assetId: id });
    return true;
  } catch {
    return false;
  }
}
function cue(id: Cue, web: () => void): void {
  if (!NATIVE) { web(); return; }
  void playNative(id).then((ok) => { if (!ok) web(); });
}

export function playPeriodChange(): void {
  cue('period', () => {
    beep(523, 0.12, 0.85);
    setTimeout(() => beep(659, 0.12, 0.5), 140);
    setTimeout(() => beep(784, 0.22, 0.6), 280);
  });
  vibrate([200, 100, 200]);
}
export function playWarning(): void {
  cue('warning', () => {
    beep(880, 0.18, 0.88, 'square');
    setTimeout(() => beep(880, 0.18, 0.45, 'square'), 260);
  });
  vibrate([150, 80, 150]);
}
export function playDone(): void {
  cue('done', () => {
    beep(784, 0.2, 0.85);
    setTimeout(() => beep(659, 0.2, 0.5), 220);
    setTimeout(() => beep(523, 0.4, 0.6), 440);
  });
  vibrate([300, 100, 300, 100, 500]);
}
